//
// Merge two objects, and merge any arrays within those objects
// by position. This is to help with merging Apache EChart option
// objects.
//

export function deepMerge<T, U>(target: T, source: U): T {
	let output: any = { ...target };

	if (isObject(target) && isObject(source)) {
		output = mergeObjects(target!, source!);
	} else if (Array.isArray(target) && Array.isArray(source)) {
		output = mergeArraysByPosition(target, source);
	} else {
		output = source;
	}
	return output as T;
}

function isObject(item: any): boolean {
	return item && typeof item === "object" && !Array.isArray(item);
}

function mergeObjects<T extends object, U extends object>(target: T, source: U): T {
	let output: any = { ...target };

	Object.keys(source as keyof object).forEach((key) => {
		if (isObject(source[key as keyof object])) {
			if (!(key in target) || !isObject(target[key as keyof object])) {
				output[key] = source[key as keyof object];
			} else {
				output[key] = deepMerge(target[key as keyof object], source[key as keyof object]);
			}
		} else if (
			Array.isArray(source[key as keyof object]) &&
			Array.isArray(target[key as keyof object])
		) {
			output[key] = mergeArraysByPosition(
				target[key as keyof object],
				source[key as keyof object]
			);
		} else {
			output[key] = source[key as keyof object];
		}
	});
	return output as T;
}

function mergeArraysByPosition<T>(target: T[], source: T[]): T[] {
	const mergedArray: T[] = [];
	const maxLength = Math.max(target.length, source.length);

	for (let i = 0; i < maxLength; i++) {
		if (target[i] && source[i]) {
			if (isObject(target[i]) && isObject(source[i])) {
				mergedArray.push(deepMerge(target[i], source[i]));
			} else {
				mergedArray.push(source[i]);
			}
		} else if (source[i]) {
			mergedArray.push(source[i]);
		} else if (target[i]) {
			mergedArray.push(target[i]);
		}
	}
	return mergedArray;
}

/*
// Example Usage:
const obj1 = {
	a: 1,
	b: { c: 2, d: 3 },
	e: [{ x: 1 }, { y: 2 }],
};

const obj2 = {
	a: 10,
	b: { d: 4, e: 5 },
	e: [{ z: 3 }, { w: 4 }],
	f: 6,
};

const mergedObj = deepMerge(obj1, obj2);
console.log(mergedObj);
*/
/* Output:
{
  a: 10,
  b: { c: 2, d: 4, e: 5 },
  e: [ { x: 1, z: 3 }, { y: 2, w: 4 } ],
  f: 6
}
*/
